/**
 * WareBee Digital Twin - Warehouse Summary KPIs
 * Top-level KPIs providing overall warehouse health metrics
 */

import { ActivityEventProcessType } from '@warebee/shared/import-converter';

/**
 * Warehouse Summary KPIs
 */
export const warehouseSummaryKpis /*: WarehouseKPI[] */ = [
  {
    id: 'overall_warehouse_efficiency',
    category: 'warehouse_summary',
    title: 'Overall Warehouse Efficiency',
    description:
      'Composite score of warehouse operational efficiency across all processes',
    unit: '%',
    formula:
      'AVG(process_efficiency, labor_efficiency, inventory_accuracy, order_fulfillment_rate)',
    tags: ['iso-22400:OEE', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 95,
          warning: { lower: 90 },
          critical: { lower: 85 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 85 },
            { label: 'Warning', min: 85.01, max: 90 },
            { label: 'Target', min: 90.01, max: 95 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    forecastHorizon: 'P30D',
    drillDimensions: ['process', 'shift', 'zone'],
    alertTopic: 'warehouse.alerts.summary.efficiency',
  },
  {
    id: 'order_fulfillment_rate',
    category: 'warehouse_summary',
    title: 'Order Fulfillment Rate',
    description: 'Percentage of orders fulfilled completely and on time',
    unit: '%',
    formula:
      '(COUNT(orders WHERE status = "fulfilled_complete_ontime") / COUNT(orders)) * 100',
    tags: ['iso-22400:QAS', 'quality'],
    benchmark: {
      standards: {
        site: {
          target: 98,
          warning: { lower: 95 },
          critical: { lower: 90 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 90 },
            { label: 'Warning', min: 90.01, max: 95 },
            { label: 'Target', min: 95.01, max: 98 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    forecastHorizon: 'P7D',
    drillDimensions: ['client_id', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.summary.fulfillment',
    schemaReferences: {
      tables: ['orderSet'],
      fields: {
        orderSet: ['orderId', 'loadByDate', 'orderDate'],
      },
    },
    processType: ActivityEventProcessType.DISPATCH,
  },
  {
    id: 'warehouse_throughput',
    category: 'warehouse_summary',
    title: 'Warehouse Throughput',
    description: 'Total volume of orders processed per day',
    unit: 'orders/day',
    formula:
      'COUNT(orders WHERE status IN ("shipped", "fulfilled") AND date = current_date)',
    tags: ['throughput'],
    benchmark: {
      standards: {
        site: {
          target: 500,
          warning: { lower: 450 },
          critical: { lower: 400 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 400 },
            { label: 'Warning', min: 400.01, max: 450 },
            { label: 'Target', min: 450.01, max: 500 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    forecastHorizon: 'P7D',
    drillDimensions: ['order_type', 'shipping_method', 'hour_of_day'],
    alertTopic: 'warehouse.alerts.summary.throughput',
    schemaReferences: {
      tables: ['orderSet', 'activityFeed'],
      fields: {
        orderSet: ['orderId', 'orderDate'],
        activityFeed: ['eventProcessType', 'eventEndTime'],
      },
      joins: [
        {
          from: 'activityFeed',
          to: 'orderSet',
          on: ['activityFeed.jobId = orderSet.orderId'],
        },
      ],
    },
    processType: ActivityEventProcessType.SHIPPING,
  },
  {
    id: 'cost_per_order',
    category: 'warehouse_summary',
    title: 'Cost Per Order',
    description:
      'Average total cost to process an order from receipt to shipment',
    unit: '$',
    formula: 'SUM(labor_cost + equipment_cost + overhead_cost) / COUNT(orders)',
    tags: ['cost'],
    benchmark: {
      standards: {
        site: {
          target: 12.5,
          warning: { upper: 15 },
          critical: { upper: 18 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 18 },
            { label: 'Warning', min: 18.01, max: 15 },
            { label: 'Target', min: 15.01, max: 12.5 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    forecastHorizon: 'P14D',
    drillDimensions: ['order_complexity', 'order_value', 'client_id'],
    alertTopic: 'warehouse.alerts.summary.cost',
  },
  {
    id: 'total_orders',
    category: 'warehouse_summary',
    title: 'Total Orders',
    description: 'Total number of orders processed in the period',
    unit: 'orders',
    formula: 'COUNT(orders)',
    tags: ['orders', 'volume'],
    benchmark: {
      standards: {
        site: {
          target: 3000,
          warning: { lower: 2500 },
          critical: { lower: 2000 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 2000 },
            { label: 'Warning', min: 2000.01, max: 2500 },
            { label: 'Target', min: 2500.01, max: 3000 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.summary.total_orders',
  },
  {
    id: 'orders_completed',
    category: 'warehouse_summary',
    title: 'Orders Completed',
    description: 'Number of orders completed in the period',
    unit: 'orders',
    formula: 'COUNT(orders WHERE status = "completed")',
    tags: ['orders', 'completion'],
    benchmark: {
      standards: {
        site: {
          target: 2800,
          warning: { lower: 2400 },
          critical: { lower: 2000 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 2000 },
            { label: 'Warning', min: 2000.01, max: 2400 },
            { label: 'Target', min: 2400.01, max: 2800 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.summary.orders_completed',
  },
  {
    id: 'order_completion',
    category: 'warehouse_summary',
    title: 'Order Completion %',
    description: 'Percentage of orders completed out of total orders',
    unit: '%',
    formula: '(COUNT(orders WHERE status = "completed") / COUNT(orders)) * 100',
    tags: ['orders', 'completion', 'percentage'],
    benchmark: {
      standards: {
        site: {
          target: 95,
          warning: { lower: 90 },
          critical: { lower: 85 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 85 },
            { label: 'Warning', min: 85.01, max: 90 },
            { label: 'Target', min: 90.01, max: 95 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.summary.order_completion',
  },
  {
    id: 'shipping_efficiency',
    category: 'warehouse_summary',
    title: 'Shipping Efficiency',
    description: 'Efficiency of shipping operations',
    unit: '%',
    formula: 'AVG(shipping_efficiency)',
    tags: ['shipping', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 97,
          warning: { lower: 95 },
          critical: { lower: 90 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 90 },
            { label: 'Warning', min: 90.01, max: 95 },
            { label: 'Target', min: 95.01, max: 97 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['carrier', 'zone'],
    alertTopic: 'warehouse.alerts.summary.shipping_efficiency',
  },
  {
    id: 'active_issues',
    category: 'warehouse_summary',
    title: 'Active Issues',
    description: 'Number of active operational issues',
    unit: 'issues',
    formula: 'COUNT(issues WHERE status = "active")',
    tags: ['issues', 'operations'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 3 },
          critical: { upper: 5 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 3 },
            { label: 'Target', min: 3.01, max: 0 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['issue_type'],
    alertTopic: 'warehouse.alerts.summary.active_issues',
  },
  {
    id: 'congestion',
    category: 'warehouse_summary',
    title: 'Congestion',
    description: 'Average congestion level in warehouse zones',
    unit: '%',
    formula: 'AVG(congestion_level)',
    tags: ['congestion', 'operations'],
    benchmark: {
      standards: {
        site: {
          target: 2,
          warning: { upper: 5 },
          critical: { upper: 10 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 5 },
            { label: 'Target', min: 5.01, max: 2 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['zone'],
    alertTopic: 'warehouse.alerts.summary.congestion',
  },
  {
    id: 'stockouts',
    category: 'warehouse_summary',
    title: 'Stockouts',
    description: 'Average number of stockouts in the period',
    unit: 'incidents',
    formula: 'COUNT(stockouts)',
    tags: ['stockouts', 'inventory'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 2 },
          critical: { upper: 5 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 2 },
            { label: 'Target', min: 2.01, max: 0 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['sku', 'zone'],
    alertTopic: 'warehouse.alerts.summary.stockouts',
  },
  {
    id: 'delayed_shipments',
    category: 'warehouse_summary',
    title: 'Delayed Shipments',
    description: 'Number of shipments delayed beyond SLA',
    unit: 'shipments',
    formula: 'COUNT(shipments WHERE status = "delayed")',
    tags: ['shipments', 'delay'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 2 },
          critical: { upper: 5 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 2 },
            { label: 'Target', min: 2.01, max: 0 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['carrier', 'zone'],
    alertTopic: 'warehouse.alerts.summary.delayed_shipments',
  },
  {
    id: 'quality_issues',
    category: 'warehouse_summary',
    title: 'Quality Issues',
    description: 'Number of quality-related issues reported',
    unit: 'issues',
    formula: 'COUNT(issues WHERE type = "quality")',
    tags: ['quality', 'issues'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 1 },
          critical: { upper: 3 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 3 },
            { label: 'Warning', min: 3.01, max: 1 },
            { label: 'Target', min: 1.01, max: 0 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['issue_type'],
    alertTopic: 'warehouse.alerts.summary.quality_issues',
  },
  {
    id: 'system_downtime',
    category: 'warehouse_summary',
    title: 'System Downtime',
    description: 'Total system downtime in hours',
    unit: 'hours',
    formula: 'SUM(downtime_hours)',
    tags: ['system', 'downtime'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 1 },
          critical: { upper: 3 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 3 },
            { label: 'Warning', min: 3.01, max: 1 },
            { label: 'Target', min: 1.01, max: 0 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['system_component'],
    alertTopic: 'warehouse.alerts.summary.system_downtime',
  },
];

export default warehouseSummaryKpis;
